import { Viewer, ViewerProps } from "../main";
import { RenderUtils } from "../rendering";
import {
  Color,
  Line3,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  Vector3,
  WebGLRenderer
} from "three";
import { MouseControls } from ".";
import onresize from "resize-event";
import { CameraImage, PanoramicImage, PlanarImage, CameraArguments } from "../cameras";
import { getPointScale } from "../utilities";
import { RayCaster } from "../ray-caster";

export interface IntersectResponse {
  error: number;
  distances: [number, number];
  calculated: boolean;
  valid: boolean;
  message: any;
}

interface Coordinate {
  position: Vector3
  direction: Vector3
}

export interface MultiImageOptions {
  canComplete?: boolean
  canAddMore?: boolean
  disableAddMore?: boolean
  disableComplete?: boolean
  disablePreviousImage?: boolean
  disableNextImage?: boolean
  coordinates?: [Coordinate | null, Coordinate | null]
  intersect?: null | IntersectResponse
}


// TODO: replace with actual function requirements

interface WindowLoadOptions {
  onClose?: () => void;
  onComplete?: () => void;
  onAddPoint?: () => void;
  canComplete: boolean;
  canAddMore: boolean;
}

interface SavedCameraState {
  fov: number;
  angles: number[];
}

export class MultiImageWindow {
  private viewer: Viewer;
  private cameraScene: Scene;
  public objectScene: Scene;
  private backgroundScene: Scene;
  private mouseControls: MouseControls;
  public camera: PerspectiveCamera
  private renderer: WebGLRenderer;
  private renderUtils: RenderUtils;
  private containerElement;
  private activeCamera: CameraImage
  private tools: MultipleCameraMeasurement;

  private setMultiImageOpen: React.Dispatch<boolean>;
  private setMultiImageState: React.Dispatch<MultiImageOptions>;

  public onComplete: Function;
  public onAddPoint: Function;
  public onClose: Function;

  private intersectResponse = null;
  private pixelRatio = 1.0;
  private minCameraDistance = 2.0;
  private enabled = false;
  public canComplete = false;
  private canAddMore = false;
  private disablePreviousImage = false;
  private disableNextImage = false;
  public disableAddMore = false;
  public disableComplete = false;
  private focused = false;
  private oldCameraState: SavedCameraState;
  private cameraIndex;
  private observer;

  constructor(viewer: Viewer, props: ViewerProps) {
    const {setMultiImageOpen, setMultiImageState} = props;

    this.viewer = viewer;
    this.setMultiImageOpen = setMultiImageOpen;
    this.setMultiImageState = setMultiImageState;

    this.initRenderer();
    this.initEvents();

    this.mouseControls = new MouseControls(this.viewer, this.camera);
    this.tools = new MultipleCameraMeasurement(viewer, this);
  }

  get width(): number {
    return this.parentElement
      ? this.parentElement.offsetWidth
      : 0;
  }

  get height(): number {
    return this.parentElement
      ? this.parentElement.offsetHeight
      : 0;
  }

  get aspect() {
    return this.width / this.height;
  }

  get lookat() {
    return this.mouseControls.lookat
      .add(this.camera.position);
  }

  get parentElement() {
    return this.containerElement?.parentElement;
  }

  init(container) {
    this.containerElement = container;
    this.containerElement.appendChild(this.renderer.domElement);

    this.initResize();
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.removeEvents();
  }

  update() {
    const state = {
      canComplete: this.canComplete,
      canAddMore: this.canAddMore,
      disableAddMore: this.disableAddMore,
      disableComplete: this.disableComplete,
      disablePreviousImage: this.disablePreviousImage,
      disableNextImage: this.disableNextImage,
      coordinates: this.tools.coordinates,
      intersect: this.intersectResponse
    } as MultiImageOptions;

    this.setMultiImageState(state);
  }

  updateIntersect() {
    this.intersectResponse = this.tools.calculate();
    this.update();
  }

  updateBackground(color: string) {
    this.backgroundScene.background = new Color(color);
  }

  show(options: WindowLoadOptions) {
    const {
      canComplete = true,
      canAddMore = true,
      onComplete = () => {},
      onAddPoint = () => {},
      onClose = () => {}
    } = options;

    this.canComplete = canComplete;
    this.canAddMore = canAddMore;
    this.onComplete = onComplete;
    this.onAddPoint = onAddPoint;
    this.onClose = onClose;

    if (this.enabled) {
      this.update();
    } else {
      this.setMultiImageOpen(true);
    }
  }

  loadNextImage() {
    const numCameras = this.viewer.cameraHandler.cameras.length;
    const index = (this.cameraIndex + 1) % numCameras;
    this.loadCamera(index);
  }

  loadPreviousImage() {
    const numCameras = this.viewer.cameraHandler.cameras.length;
    const index = ((this.cameraIndex - 1) + numCameras) % numCameras;
    this.loadCamera(index);
  }

  async loadCamera(index) {
    this.cameraIndex = index;

    this.resetSecondObservation();
    this.disablePreviousImage = true;
    this.disableNextImage = true;
    this.update();

    const nextCamera = this.viewer.cameraHandler.cameras[index];
    const camera = this.getNewCamera(nextCamera);

    // Load new camera
    console.log(`Load camera: ${camera.name}`);

    // Load image data from path
    camera.initialize();
    await camera.load();

    // Update previous texture
    await this.renderUtils.updateTexture();

    // Destroy previously loaded camera
    this.activeCamera?.destroy();
    this.activeCamera = camera;

    camera.activate();

    this.updateIntersect();

    this.disablePreviousImage = false;
    this.disableNextImage = false;
    this.update();
  }

  /** @private */
  getNewCamera(nextCamera: CameraImage): CameraImage {
    const args = [
      nextCamera.cameraFile,
      nextCamera.cameraData,
      this.cameraScene,
      this.camera,
      this.mouseControls,
    ] as CameraArguments;

    let camera;
    if (nextCamera instanceof PanoramicImage) {
      camera = new PanoramicImage(...args);
    } else if (nextCamera instanceof PlanarImage) {
      camera = new PlanarImage(...args);
    }

    return camera;
  }

  setState(state) {
    this.enabled = state;

    if (!state) {
      this.resetObservations();
      this.activeCamera?.destroy();
      this.activeCamera = null;
    }
  }

  close() {
    this.setMultiImageOpen(false);
  }

  initResize() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = onresize(this.parentElement, () => {
      this.handleWindowResize();
    });

    this.handleWindowResize();
  }

  initEvents() {
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseWheel = this.onMouseWheel.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onMouseFocus = this.onMouseFocus.bind(this);

    const element = this.renderer.domElement;
    element.addEventListener('mousedown', this.onMouseDown, false);
    element.addEventListener('mouseup', this.onMouseUp, false);
    element.addEventListener('mousemove', this.onMouseMove, false);
    element.addEventListener('wheel', this.onMouseWheel, false);
    element.addEventListener('mouseover', this.onMouseFocus, false);
    element.addEventListener('mouseout', this.onMouseFocus, false);

    // Keydown doesnt work on canvas
    window.addEventListener('keydown', this.onKeyDown, false);
    window.addEventListener('keyup', this.onKeyUp, false);
  }

  removeEvents() {
    const element = this.renderer.domElement;
    element.removeEventListener('mousedown', this.onMouseDown, false);
    element.removeEventListener('mouseup', this.onMouseUp, false);
    element.removeEventListener('mousemove', this.onMouseMove, false);
    element.removeEventListener('wheel', this.onMouseWheel, false);
    element.removeEventListener('mouseover', this.onMouseFocus, false);
    element.removeEventListener('mouseout', this.onMouseFocus, false);

    window.removeEventListener('keydown', this.onKeyDown, false);
    window.removeEventListener('keyup', this.onKeyUp, false);
  }

  initRenderer() {
    this.renderer = new WebGLRenderer({
      alpha: true,
    });

    this.renderer.autoClear = false;
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.domElement.id = "multi-image-canvas";

    this.camera = new PerspectiveCamera(90);
    this.camera.near = 0.1;
    this.camera.far = 10;
    this.camera.up = new Vector3(0, 0, 1);

    this.cameraScene = new Scene();
    this.objectScene = new Scene();
    this.backgroundScene = new Scene();

    this.renderUtils = new RenderUtils(
      this.renderer,
      this.camera,
      this.pixelRatio
    );
  }

  onKeyDown(event) {
    if (!this.focused) return;
    event.preventDefault();

    if (event.key === "Shift") {
      this.onZoomStart();
    }
  }

  onKeyUp(event) {
    if (event.key === "Shift") {
      this.onZoomFinish();
    }
  }

  onMouseFocus(event) {
    if (event.type === 'mouseover') {
      this.focused = true;
    } else if (event.type === 'mouseout') {
      this.focused = false;
    }
  }

  onMouseDown(event) {
    event.preventDefault();
    this.mouseControls.onMouseDown(event);
  }

  onMouseUp(initialEvent) {
    if (!this.mouseControls.down || !this.enabled) return;

    const event = this.mouseControls.onMouseUp(initialEvent);
    if (event.mouseMoved || event.isRightClick) return;

    const raycaster = new RayCaster(this, event);
    const direction = raycaster.direction;
    const position = raycaster.position;
    this.addSecondObservation(position, direction);
  }

  onMouseMove(initialEvent) {
    if (!this.enabled) return;

    const event = this.mouseControls.onMouseMove(initialEvent);

    this.renderUtils.onMouseMove(event);

    if (!event.mouseMoved || !event.mouseDown) return;

    this.mouseControls.updateRotation(
      this.camera.fov,
      this.width,
      this.height
    );
  }

  onMouseWheel(event) {
    if (!this.enabled) return false;

    this.mouseControls.onMouseScroll();
    this.mouseControls.onMouseWheel(event);
  }

  /** @private */
  async onZoomStart() {
    if (this.renderUtils.zooming) return;

    await this.renderUtils.updateTexture();
    this.renderUtils.zoomToCursor = true;
    this.mouseControls.zoomToCursor = true;
    await this.saveLastPosition();
  }

  /** @private */
  onZoomFinish() {
    if (!this.renderUtils.zooming) return;
    this.renderUtils.zoomToCursor = false;
    this.mouseControls.zoomToCursor = false;
    this.loadLastPosition();
  }

  /** @private */
  async saveLastPosition() {
    // Save previous state
    this.oldCameraState = {
      fov: this.camera.fov,
      angles: this.mouseControls.angles
    };

    // Start slightly more zoomed in
    this.mouseControls.zoom(this.camera.fov * 0.80);
  }

  /** @private */
  loadLastPosition() {
    const {fov, angles,} = this.oldCameraState;

    // Restore original FOV
    this.mouseControls.fov = fov;
    this.mouseControls.updateCameraMatrix();

    // Restore original position
    this.mouseControls.angles = angles;
  }

  getClickedPoint() {
    return this.tools.getClickedPoint();
  }

  resetObservations() {
    this.resetFirstObservation();
    this.resetSecondObservation();
  }

  resetFirstObservation() {
    this.tools.resetFirstObservation();
    this.updateIntersect();
  }

  resetSecondObservation() {
    this.tools.resetSecondObservation();
    this.updateIntersect();
  }

  /** @private */
  getBestCamera(position: Vector3, direction: Vector3) {
    const currentIndex = this.viewer.getCurrentCameraIndex();
    const cameras = this.viewer.cameraHandler.cameras;
    const numCameras = cameras.length;

    let increment;
    let nextIndex;

    if (currentIndex === 0) {
      // Only valid direction is forwards
      increment = 1;
    } else if (currentIndex === (numCameras - 1)) {
      // Only valid direction is backwards
      increment = -1;
    } else {
      // Determine direction by creating a line
      // and projecting our clicked point on it
      let nextIndex = ((currentIndex + 1) % numCameras) % numCameras;
      let clickedPoint = new Vector3().copy(position).add(direction);
      let nextPosition = cameras[nextIndex].position.scene;

      let nextLine = new Line3(position, nextPosition);
      let alongLine = nextLine.closestPointToPointParameter(clickedPoint);
      increment = (alongLine > 0.0) ? 1 : -1;
    }

    for (let i=1; i<10; i++) {
      nextIndex = currentIndex + i*increment;

      // Reached the start / end of list, exit loop early
      if ((nextIndex === 0) || (nextIndex === (numCameras - 1))) {
        break;
      }

      let newPosition = cameras[nextIndex].position.scene;
      let distance = position.distanceTo(newPosition);
      if (distance >= this.minCameraDistance) {
        break;
      }
    }

    this.loadCamera(nextIndex);
  }

  addFirstObservation(position: Vector3, direction: Vector3) {
    this.tools.addFirstObservation(position, direction);
    this.updateIntersect();

    if (!this.activeCamera) {
      this.getBestCamera(position, direction);
    }
  }

  addSecondObservation(position: Vector3, direction: Vector3) {
    this.tools.addSecondObservation(position, direction);
    this.updateIntersect();
  }

  handleWindowResize() {
    this.camera.aspect = this.aspect;
    this.camera.updateProjectionMatrix();

    this.renderer.domElement.style.display = "none";
    this.renderer.setSize(this.width, this.height);
    this.renderer.domElement.style.display = "block";

    // Re-render is required or else the div will flash white
    this.render();
  }

  updateMaterial(material, values) {
    let keys = Object.keys(values);
    keys.forEach(key => {
      material[key] = values[key];
    });
  }

  renderSceneObjects() {
    // Render background color and image data
    this.renderUtils.setEDLState(false);
    this.renderUtils.setRenderTarget(true);
    this.renderUtils.renderScene(this.backgroundScene);
    this.renderUtils.renderScene(this.cameraScene);
    this.renderUtils.renderToTarget(1.0);

    // Render objects
    this.renderUtils.setEDLState(true);
    this.renderUtils.setRenderTarget(true);
    this.renderUtils.renderScene(this.objectScene);
    this.renderUtils.renderToTarget(1.0);

    // Render target to screen
    this.renderUtils.renderToScreen();

    // Update previous texture
    this.renderUtils.renderToPrevious();
  }

  render() {
    if (!this.enabled) return false;

    this.mouseControls.update();
    this.tools.update();
    this.renderUtils.update();

    this.renderSceneObjects();
  }
};

class MultipleCameraMeasurement {
  private viewer: Viewer;
  private window: MultiImageWindow;
  public coordinates = [null, null];
  private firstPoint = null;
  private secondPoint = null;
  private sphereGeometry = new SphereGeometry(0.05, 20, 20);
  private sphereMaterial = new MeshBasicMaterial({color: 0xe6e600});

  constructor(viewer, window) {
    this.viewer = viewer;
    this.window = window;
  }

  get viewerScene() {
    return this.viewer.measurements.scene;
  }

  get viewerCamera() {
    return this.viewer.camera;
  }

  get windowScene() {
    return this.window.objectScene;
  }

  get windowCamera() {
    return this.window.camera;
  }

  get sameCamera() {
    const origin1 = this.window.camera?.position;
    const origin2 = this.viewer.camera?.position;

    if (origin1 && origin2) {
      if (origin1.distanceTo(origin2) < 0.005) {
        return true;
      }
    }

    return false;
  }

  getEndpoint(origin, direction) {
    return direction
      .clone()
      .setLength(1.0)
      .add(origin);
  }

  getSphereMesh(position) {
    let sphere = new Mesh(this.sphereGeometry, this.sphereMaterial);
    sphere.position.copy(position);

    return sphere;
  }

  getClickedPoint() {
    let rayA = this.coordinates[0];
    let rayB = this.coordinates[1];
    if ((rayA === null) || (rayB === null)) {
      return;
    }

    if (this.sameCamera) {
      return;
    }

    let {point, valid} = this.getIntersection(rayA, rayB);

    if (valid) {
      return point;
    }
  }

  setButtonDisabled(state) {
    this.window.disableComplete = state;
    this.window.disableAddMore = state;
  }

  calculate() {
    const defaultResponse = {
      error: null,
      distances: [0, 0],
      calculated: false,
      valid: true,
      message: null
    };

    if (this.sameCamera) {
      this.setButtonDisabled(true);
      return {
        ...defaultResponse,
        valid: false,
        message: "Measuring from same camera"
      };
    } else {
      this.setButtonDisabled(false);
    }

    let rayA = this.coordinates[0];
    let rayB = this.coordinates[1];
    if ((rayA === null) || (rayB === null)) {
      return defaultResponse;
    }

    let {error, distances, valid, message}
      = this.getIntersection(rayA, rayB);

    this.setButtonDisabled(!valid);

    if (!valid) {
      return {...defaultResponse, message, valid: false};
    }

    return {
      ...defaultResponse,
      error,
      distances,
      calculated: true
    };
  }

  resetFirstObservation() {
    if (this.firstPoint) {
      this.viewerScene.remove(this.firstPoint);
    }
    this.coordinates[0] = null;
    this.firstPoint = null;
  }

  resetSecondObservation() {
    if (this.secondPoint) {
      this.windowScene.remove(this.secondPoint);
    }
    this.coordinates[1] = null;
    this.secondPoint = null;
  }

  addFirstObservation(position, direction) {
    this.viewerScene.remove(this.firstPoint);

    let origin = position.clone();
    let endPoint = this.getEndpoint(origin, direction);
    this.coordinates[0] = {origin, direction};

    this.firstPoint = this.getSphereMesh(endPoint);
    this.viewerScene.add(this.firstPoint);
    this.updatePointScale();
  }

  addSecondObservation(position, direction) {
    this.windowScene.remove(this.secondPoint);

    let origin = position.clone();
    let endPoint = this.getEndpoint(origin, direction);
    this.coordinates[1] = {origin, direction};

    this.secondPoint = this.getSphereMesh(endPoint);
    this.windowScene.add(this.secondPoint);
    this.updatePointScale();
  }

  setPointScale(mesh: Mesh, camera: PerspectiveCamera) {
    if (!mesh) return;
    const scale = getPointScale(camera, mesh);
    mesh.scale.set(scale, scale, scale);
    mesh.geometry.computeBoundingSphere();
  }

  updatePointScale() {
    this.setPointScale(this.firstPoint, this.viewerCamera);
    this.setPointScale(this.secondPoint, this.windowCamera);
  }

  /** Get closest possible midpoint for two line segments */
  getIntersection(rayA, rayB) {
    let message = "";
    let valid = true;

    let Nv = rayA.direction.clone().cross(rayB.direction);
    let Na = rayA.direction.clone().cross(Nv).normalize();
    let Nb = rayB.direction.clone().cross(Nv).normalize();
    let Da = rayA.direction.clone().normalize();
    let Db = rayB.direction.clone().normalize();
    let da = rayB.origin.clone().sub(rayA.origin).dot(Nb) / Da.dot(Nb);
    let db = rayA.origin.clone().sub(rayB.origin).dot(Na) / Db.dot(Na);
    let ptA = rayA.origin.clone().add(Da.multiplyScalar(da));
    let ptB = rayB.origin.clone().add(Db.multiplyScalar(db));

    // Get our midpoint and segment length
    let lineSegment = new Line3(ptA, ptB);
    let segmentLength = lineSegment.distance();
    let midPoint = new Vector3();
    lineSegment.getCenter(midPoint);

    // Make sure our result is in front of us
    const d1n = ptA.clone().sub(rayA.origin).setLength(1.0);
    const d2n = ptB.clone().sub(rayB.origin).setLength(1.0);
    const a1 = Math.abs(rayA.direction.angleTo(d1n));
    const a2 = Math.abs(rayB.direction.angleTo(d2n));
    const behindCamera = (a1 > 0.005) || (a2 > 0.005);
    if (behindCamera) {
      valid = false;
      message = "Intersection point behind camera";
    }

    let distances = [
      ptA.distanceTo(rayA.origin),
      ptB.distanceTo(rayB.origin)
    ];

    return {
      point: midPoint,
      error: segmentLength,
      distances,
      valid,
      message
    };
  }

  update() {
    this.updatePointScale();
  }
}
